import differenceBy from 'lodash/differenceBy';
import * as actions from '@xmatters/redux/actions/addUsers';
import {
  GET_PERSON_PROPERTY_DEFINITIONS_DEFINED_ORDER_START,
  GET_PERSON_PROPERTY_DEFINITIONS_DEFINED_ORDER_SUCCESS,
} from 'actions/personPropertyDefinitions';
import { GET_PERSON_SUCCESS } from 'actions/loggedInUser';
import { GET_XMAPI_ROLES_SUCCESS } from 'actions/roles';
import {
  LOAD_SERVICE_PROVIDERS_SUCCESS,
  LOAD_SERVICE_PROVIDERS_FAIL,
} from 'actions/serviceProviders';
import { LOAD_SITES_START, LOAD_SITES_SUCCESS } from 'actions/sites';

export const MAX_ADDED_USERS_PER_SESSION = 40;

export const modalDefaultState = {
  // list of users currently being added in bulk
  addedUsersInProgress: [],
  // list of users to be shown on newly added users list
  recentlyAddedUsers: [],
  isOneUserFromSessionLimit: false,
  createAnotherUser: false,
  copySettings: false,
};

export const defaultState = {
  formOptions: {
    roleOptions: [],
    siteOptions: [],
  },
  isOpen: false,
  createAnotherUser: false,
  copySettings: false,
  siteOptionsLoading: true,
  defaultServiceProvider: null,
  serviceProvidersLoading: true,
  canCopySettings: true,
  ...modalDefaultState,
};

const getRoleOptions = roles =>
  roles?.map(role => ({ label: role.name, value: role.name })) || [];

const getRoleOptionsByRoleOnRolePermissions = roles =>
  roles
    ?.filter(
      role =>
        role.roleOnRolePermissions.includes('ASSIGN_ROLE') &&
        role.name !== 'Company Admin',
    )
    ?.map(role => ({ label: role.name, value: role.name })) || [];

const getSiteOptions = sites =>
  sites?.map(site => ({
    label: site.name,
    value: site.id,
    language: site.language,
    timezone: site.timezone,
  })) || [];

export default function addUsers(state = defaultState, action) {
  switch (action.type) {
    case GET_XMAPI_ROLES_SUCCESS:
      return {
        ...state,
        formOptions: {
          ...state.formOptions,
          roleOptions: getRoleOptions(action.payload.data),
        },
      };
    case GET_PERSON_SUCCESS:
      return {
        ...state,
        formOptions: {
          ...state.formOptions,
          roleOptionsByRoleOnRolePermissions:
            getRoleOptionsByRoleOnRolePermissions(
              action.payload.rolePermissions.data,
            ),
        },
      };
    case LOAD_SITES_START:
      return {
        ...state,
        siteOptionsLoading: true,
      };
    case LOAD_SITES_SUCCESS:
      return {
        ...state,
        siteOptionsLoading: false,
        formOptions: {
          ...state.formOptions,
          siteOptions: getSiteOptions(action.payload.data),
        },
      };
    case LOAD_SERVICE_PROVIDERS_SUCCESS:
      return {
        ...state,
        defaultServiceProvider: action?.payload?.data?.[0],
        serviceProvidersLoading: false,
      };
    case LOAD_SERVICE_PROVIDERS_FAIL:
      return {
        ...state,
        serviceProvidersLoading: false,
      };
    case actions.USERS_LIST_PAGE_POST_USER_SUCCESS: {
      const newArr = [...state.addedUsersInProgress];
      newArr.push(action.payload);
      return {
        ...state,
        addedUsersInProgress: newArr,
        isOneUserFromSessionLimit:
          newArr.length >= MAX_ADDED_USERS_PER_SESSION - 1,
      };
    }
    case actions.POPULATE_RECENTLY_ADDED_USERS: {
      return {
        ...state,
        addedUsersInProgress: [],
        recentlyAddedUsers: state.addedUsersInProgress,
        isOneUserFromSessionLimit: MAX_ADDED_USERS_PER_SESSION - 1 <= 0,
      };
    }
    case actions.REMOVE_RECENTLY_ADDED_USERS: {
      return {
        ...state,
        recentlyAddedUsers: differenceBy(
          state.recentlyAddedUsers,
          action.payload,
          'id',
        ),
      };
    }
    case actions.CLEAR_RECENTLY_ADDED_USERS: {
      return {
        ...state,
        recentlyAddedUsers: [],
      };
    }
    case GET_PERSON_PROPERTY_DEFINITIONS_DEFINED_ORDER_START: {
      return {
        ...state,
        customProperties: {
          data: [],
          loading: true,
        },
      };
    }
    case GET_PERSON_PROPERTY_DEFINITIONS_DEFINED_ORDER_SUCCESS: {
      const customProperties = action.payload.data;
      const requiredBooleanProperties = customProperties
        .filter(prop => prop.propertyType === 'BOOLEAN' && prop.required)
        .reduce((acc, cur) => ({ ...acc, [cur.name]: false }), {});
      return {
        ...state,
        customProperties: {
          data: customProperties,
          loading: false,
        },
        requiredBooleanProperties,
      };
    }
    case actions.OPEN_DIALOG: {
      return {
        ...state,
        isOpen: true,
        ...modalDefaultState,
      };
    }
    case actions.CLOSE_DIALOG: {
      return {
        ...state,
        isOpen: false,
      };
    }
    case actions.SET_CREATE_ANOTHER_USER: {
      return {
        ...state,
        createAnotherUser: action.payload,
      };
    }
    case actions.SET_COPY_SETTINGS: {
      return {
        ...state,
        copySettings: action.payload,
      };
    }
    case actions.SET_CAN_COPY_SETTINGS: {
      return {
        ...state,
        canCopySettings: action.payload,
      };
    }
    default:
      return state;
  }
}
